/**
 * 🚀 NEW NAV CREATOR 2025 - Modern Navigation System
 * 
 * Einfaches, modernes Navigation-System für Pages
 * - Logo Upload
 * - Navigation Links (Text + URL)
 * - Social Media Icons
 * - CTA Button
 * - Responsive (Desktop/Mobile)
 * - Floating Toolbar für Settings
 */

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import NavigationFloatingToolbar from './NavigationFloatingToolbar';
import './NewNavCreator.css';

function NewNavCreator({ 
    navigationData = {}, 
    onUpdate, 
    isEditMode = true,
    isMobileView = false,
    colors = {},
    spaceFonts = {},
    isWidgetEditCollapsed = false,
    isAddWidgetsCollapsed = false
}) {
    // 🎯 State
    const [isToolbarVisible, setIsToolbarVisible] = useState(false);
    const [isSticky, setIsSticky] = useState(false); // Track if navigation should be sticky
    const [stickyWidth, setStickyWidth] = useState('100%'); // Track width for sticky mode
    const [isHidden, setIsHidden] = useState(false); // Track if navigation is hidden on scroll down
    const [lastScrollTop, setLastScrollTop] = useState(0); // Track last scroll position for hide on scroll
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // ✅ NEW: Mobile menu state
    const [portalContainer, setPortalContainer] = useState(null); // ✅ Portal container reference
    const navRef = useRef(null);
    
    // 🎨 Default Navigation Data
    const defaultNav = {
        enabled: true,
        logo: '',
        logoWidth: 120,
        logoWidthMobile: 100,
        logoHeight: 50,
        logoHeightMobile: 40,
        logoPosition: 'left', // left | center | right
        logoPositionMobile: '', // ✅ NEW: Separate Mobile Position (falls leer, wird logoPosition verwendet)
        showLogoMobile: true, // ✅ NEW: Show/Hide Logo on Mobile
        
        // Navigation Links
        navLinks: [
            { id: 'link-1', text: 'Home', url: '#home', visible: true },
            { id: 'link-2', text: 'About', url: '#about', visible: true },
            { id: 'link-3', text: 'Services', url: '#services', visible: true }
        ],
        navLinksVisible: true,
        navLinksPosition: 'center', // left | center | right
        navLinksPositionMobile: '', // ✅ NEW: Separate Mobile Position
        useMobileMenu: true, // ✅ NEW: Use hamburger menu on mobile instead of inline links
        
        // Social Media Icons
        socialIcons: [
            { id: 'social-1', platform: 'facebook', url: '', visible: false },
            { id: 'social-2', platform: 'instagram', url: '', visible: false },
            { id: 'social-3', platform: 'linkedin', url: '', visible: false },
            { id: 'social-4', platform: 'youtube', url: '', visible: false }
        ],
        socialIconsVisible: false,
        socialIconsPosition: 'right', // left | center | right
        socialIconsPositionMobile: '', // ✅ NEW: Separate Mobile Position
        showSocialIconsMobile: true, // ✅ NEW: Show/Hide Social Icons on Mobile
        
        // CTA Button Position
        ctaButtonPosition: 'right', // left | center | right
        ctaButtonPositionMobile: '', // ✅ NEW: Separate Mobile Position
        showCTAButtonMobile: true, // ✅ NEW: Show/Hide CTA Button on Mobile
        
        // CTA Button
        ctaButton: {
            enabled: false,
            text: 'Get Started',
            url: '#contact',
            backgroundColor: colors.color1 || '#5C80BC',
            textColor: '#FFFFFF',
            fontFamily: '', // ✅ Font für CTA Button
            textSize: 15, // ✅ NEW: Desktop Button Text Size
            textSizeMobile: 14 // ✅ NEW: Mobile Button Text Size
        },
        
        // Styling
        backgroundColor: '#FFFFFF',
        backgroundOpacity: 100, // ✅ NEW: Transparency für Glassmorphism (0-100%)
        height: 80,
        heightMobile: 70,
        textColor: '#333333',
        textSize: 16,
        textSizeMobile: 14,
        textFontFamily: '', // ✅ NEW: Font für Navigation Links
        sticky: false, // Fixed position on scroll
        shadow: false,
        
        // ✅ NEW: Spacing
        paddingLeft: 40,
        paddingRight: 40,
        paddingLeftMobile: 20,
        paddingRightMobile: 20,
        
        // ✅ NEW: Advanced Styling
        borderBottomEnabled: false,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
        backdropFilterEnabled: false,
        backdropFilterBlur: 10,
        hideOnScrollDown: false,
        
        // ✅ NEW: Mobile Menu Styling
        mobileMenuBackgroundColor: '#FFFFFF',
        mobileMenuTextColor: '#333333',
        mobileMenuTextSize: 16, // ✅ Font Size für Links im Mobile Menu
        mobileMenuButtonSize: 44, // ✅ Mobile Menu Hamburger Button Size (30-60px)
        mobileMenuBorderEnabled: true,
        mobileMenuBorderColor: '#e0e0e0',
        mobileMenuBorderWidth: 1,
        mobileMenuShadowEnabled: true,
        mobileMenuShadowBlur: 12, // ✅ Shadow Blur (0-30px)
        mobileMenuShadowOpacity: 15, // ✅ Shadow Opacity (0-50%)
        mobileMenuBorderRadius: 0, // ✅ Border Radius (0-30px)
        mobileMenuPadding: 20, // ✅ Menu Horizontal Padding (0-40px)
        mobileMenuLinkSpacing: 4, // ✅ Gap zwischen Links (0-16px)
        mobileMenuLinkHoverBg: 'rgba(0, 0, 0, 0.05)', // ✅ Link Hover Background
        mobileMenuLinkHoverOpacity: 5, // ✅ Hover Background Opacity (0-30%)
        mobileMenuBackdropColor: '#000000', // ✅ Backdrop Color
        mobileMenuBackdropOpacity: 50, // ✅ Backdrop Opacity (0-90%)
        mobileMenuAnimationSpeed: 300, // ✅ Animation Speed in ms (100-800ms)
        mobileMenuWidth: 80, // ✅ Menu Width in % (60-100%)
        
        // ✅ NEW: Mobile Menu Content Control
        mobileMenuShowLogo: false, // ✅ Show logo in menu (default false to avoid duplicate)
        mobileMenuLogoWidth: 100, // ✅ Logo width in mobile menu
        mobileMenuLogoHeight: 40, // ✅ Logo height in mobile menu
        mobileMenuLogoAlignment: 'center', // ✅ Logo alignment in menu (left/center/right)
        mobileMenuLogoMarginTop: 20, // ✅ Logo margin top in menu
        mobileMenuLogoMarginBottom: 20, // ✅ Logo margin bottom in menu
        mobileMenuShowLinks: true, // Show links in menu
        mobileMenuShowSocial: true, // Show social icons in menu
        mobileMenuShowCTA: true, // Show CTA button in menu
        
        // ✅ NEW: Layout Control - Symmetric vs Flexible
        symmetricLayout: true // ✅ Wenn true: Linke & rechte Sektion bekommen gleich viel Platz (mittlere Sektion bleibt perfekt zentriert)
    };
    
    // ✅ Merge mit gespeicherten Daten
    const nav = { ...defaultNav, ...navigationData };
    
    // 🎯 Find Portal Container - Einmalig beim Mount
    useEffect(() => {
        const container = document.querySelector('.page-preview') || 
                         document.querySelector('.page_preview') || 
                         document.querySelector('.page_public') || 
                         document.body;
        setPortalContainer(container);
        
        return () => {
            setPortalContainer(null);
        };
    }, []);
    
    // ✅ CRITICAL FIX: Update Sticky Width when Sidebar Toggle Changes
    useEffect(() => {
        // Nur aktualisieren wenn Navigation bereits sticky ist
        if (isSticky && nav.sticky && isEditMode) {
            const scrollContainer = document.querySelector('.page-preview') || document.querySelector('.page_preview');
            if (scrollContainer) {
                const containerWidth = scrollContainer.clientWidth;
                setStickyWidth(`${containerWidth}px`);
                console.log('🔄 Sticky Width aktualisiert nach Sidebar Toggle:', containerWidth);
            }
        }
    }, [isWidgetEditCollapsed, isAddWidgetsCollapsed, isSticky, nav.sticky, isEditMode]);
    
    // 🎯 Handle Sticky Scroll Behavior - Funktioniert in Edit Mode UND Live Preview
    useEffect(() => {
        if (!nav.sticky) {
            setIsSticky(false);
            setStickyWidth('100%'); // Reset width
            return;
        }
        
        const handleScroll = () => {
            if (!navRef.current) return;
            
            // ✅ Im Edit Mode: Finde den page-preview Container (der scrollt)
            const scrollContainer = isEditMode ? 
                document.querySelector('.page-preview') || document.querySelector('.page_preview') :
                window;
            
            let scrollTop = 0;
            
            if (isEditMode && scrollContainer !== window) {
                scrollTop = scrollContainer.scrollTop || 0;
            } else {
                scrollTop = window.scrollY || window.pageYOffset;
            }
            
            const pageStartHeight = 80; // Höhe des page_preview_header
            
            // ✅ Sticky Trigger: Nach 80px Scroll (page_preview_header ist weg)
            const shouldBeSticky = scrollTop > pageStartHeight;
            
            console.log('📊 Scroll Check:', { 
                scrollTop, 
                pageStartHeight, 
                shouldBeSticky,
                isSticky,
                isEditMode,
                scrollContainer: scrollContainer === window ? 'window' : '.page-preview'
            });
            
            // ✅ Berechne Breite BEVOR Navigation sticky wird
            if (shouldBeSticky && !isSticky) {
                // Im Edit Mode: Nimm Breite vom page_preview Container
                if (isEditMode && scrollContainer !== window) {
                    const containerWidth = scrollContainer.clientWidth;
                    setStickyWidth(`${containerWidth}px`);
                    console.log('🎯 Navigation wird sticky (Edit Mode), Container-Breite:', containerWidth);
                } else {
                    // Im Live Mode: Nimm aktuelle Nav-Breite
                    const currentWidth = navRef.current.offsetWidth;
                    setStickyWidth(`${currentWidth}px`);
                    console.log('🎯 Navigation wird sticky (Live Mode), Nav-Breite:', currentWidth);
                }
            }
            
            // ✅ BEIM HOCHSCROLLEN: Reset sticky width wenn Navigation wieder normal wird
            if (!shouldBeSticky && isSticky) {
                setStickyWidth('100%');
                setIsHidden(false); // ✅ Header wieder sichtbar wenn nicht mehr sticky
                console.log('🔄 Navigation wird normal, Breite zurückgesetzt, Header wieder sichtbar');
            }
            
            setIsSticky(shouldBeSticky);
            
            // ✅ Hide on Scroll Down Feature - NORMALES Verhalten
            // Header ist von Anfang an da, verschwindet beim Runterscrollen, erscheint beim Hochscrollen
            if (nav.hideOnScrollDown && shouldBeSticky) {
                const scrollingDown = scrollTop > lastScrollTop;
                const scrollingUp = scrollTop < lastScrollTop;
                
                // Nur verstecken wenn wir mindestens 5px gescrollt haben (verhindert Flackern)
                if (scrollingDown && scrollTop - lastScrollTop > 5) {
                    // Runterscrollen → Verstecken
                    setIsHidden(true);
                    console.log('📉 Scrolling down - Navigation versteckt');
                } else if (scrollingUp && lastScrollTop - scrollTop > 5) {
                    // Hochscrollen → Wieder einblenden (elegant von oben)
                    setIsHidden(false);
                    console.log('📈 Scrolling up - Navigation erscheint wieder');
                }
                
                setLastScrollTop(scrollTop);
            } else {
                setIsHidden(false); // Immer sichtbar wenn nicht sticky oder Feature deaktiviert
            }
        };
        
        // ✅ Im Edit Mode: Listener am page-preview Container, sonst am window
        const scrollContainer = isEditMode ? 
            document.querySelector('.page-preview') || document.querySelector('.page_preview') :
            window;
        
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            handleScroll(); // Initial check
            
            return () => {
                scrollContainer.removeEventListener('scroll', handleScroll);
            };
        }
    }, [nav.sticky, isSticky, isEditMode]);
    
    // 🎨 Active Height (responsive)
    const activeHeight = isMobileView ? nav.heightMobile : nav.height;
    
    // 🎨 Active Padding (responsive)
    const activePaddingLeft = isMobileView ? nav.paddingLeftMobile : nav.paddingLeft;
    const activePaddingRight = isMobileView ? nav.paddingRightMobile : nav.paddingRight;
    
    // 🎨 Background Color mit Opacity für Glassmorphism
    const getBackgroundColor = () => {
        if (nav.backdropFilterEnabled) {
            // Konvertiere Hex zu RGBA mit Opacity
            const hex = nav.backgroundColor || '#FFFFFF';
            const opacity = (nav.backgroundOpacity || 100) / 100;
            
            // Hex zu RGB
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        }
        return nav.backgroundColor;
    };
    
    // 🎨 Handle Logo Upload
    const handleLogoUpload = async (file) => {
        if (!file) return;
        
        // File Size Check (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            alert('File is too large. Maximum size is 2MB.');
            return;
        }
        
        try {
            // ✅ FIREBASE STORAGE UPLOAD
            const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
            const { getAuth } = await import('firebase/auth');
            
            const storage = getStorage();
            const auth = getAuth();
            
            if (!auth.currentUser) {
                alert('You must be logged in to upload images.');
                return;
            }
            
            const userId = auth.currentUser.uid;
            const timestamp = Date.now();
            const randomId = Math.random().toString(36).substr(2, 9);
            const uniqueFileName = `${timestamp}_${randomId}-${file.name}`;
            
            // Upload to Firebase Storage
            const imageRef = storageRef(storage, `pages/${userId}/navigation-logos/${uniqueFileName}`);
            const uploadTask = uploadBytesResumable(imageRef, file);

            return new Promise((resolve, reject) => {
                uploadTask.on('state_changed',
                    (snapshot) => {
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        console.log(`Upload progress: ${progress}%`);
                    },
                    (error) => {
                        console.error('Upload error:', error);
                        reject(error);
                    },
                    async () => {
                        const downloadURL = await getDownloadURL(imageRef);
                        resolve(downloadURL);
                    }
                );
            });
        } catch (error) {
            console.error('Error uploading logo:', error);
            throw error;
        }
    };
    
    // 🎨 Render Logo
    const renderLogo = () => {
        // ✅ Mobile visibility check
        if (isMobileView && nav.showLogoMobile === false) {
            return null;
        }
        
        if (!nav.logo) {
            return (
                <div className="nav-creator-logo-placeholder">
                    <i className="fas fa-image"></i>
                    <span>Logo</span>
                </div>
            );
        }
        
        return (
            <img 
                src={nav.logo} 
                alt="Logo" 
                className="nav-creator-logo"
                style={{
                    width: isMobileView ? `${nav.logoWidthMobile}px` : `${nav.logoWidth}px`,
                    height: isMobileView ? `${nav.logoHeightMobile}px` : `${nav.logoHeight}px`,
                    objectFit: 'contain'
                }}
            />
        );
    };
    
    // 🎨 Render Navigation Links
    const renderNavLinks = () => {
        if (!nav.navLinksVisible) return null;
        
        // ✅ Mobile: Show in menu or hide completely based on useMobileMenu
        if (isMobileView && nav.useMobileMenu) {
            return null; // Links werden im Mobile Menu gerendert
        }
        
        const visibleLinks = nav.navLinks.filter(link => link.visible);
        
        return (
            <div className="nav-creator-links">
                {visibleLinks.map((link) => (
                    <a 
                        key={link.id}
                        href={link.url}
                        className="nav-creator-link"
                        style={{
                            color: nav.textColor,
                            fontSize: isMobileView ? `${nav.textSizeMobile}px` : `${nav.textSize}px`,
                            fontFamily: nav.textFontFamily || 'inherit'
                        }}
                        onClick={(e) => {
                            if (isEditMode) e.preventDefault();
                        }}
                    >
                        {link.text}
                    </a>
                ))}
            </div>
        );
    };
    
    // 🎨 Render Social Icons
    const renderSocialIcons = () => {
        if (!nav.socialIconsVisible) return null;
        
        // ✅ Mobile visibility check
        if (isMobileView && nav.showSocialIconsMobile === false) {
            return null;
        }
        
        const visibleIcons = nav.socialIcons.filter(icon => icon.visible && icon.url);
        
        if (visibleIcons.length === 0) return null;
        
        const platformIcons = {
            facebook: 'fab fa-facebook',
            instagram: 'fab fa-instagram',
            linkedin: 'fab fa-linkedin',
            youtube: 'fab fa-youtube',
            twitter: 'fab fa-twitter',
            tiktok: 'fab fa-tiktok'
        };
        
        return (
            <div className="nav-creator-social">
                {visibleIcons.map((icon) => (
                    <a 
                        key={icon.id}
                        href={icon.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="nav-creator-social-icon"
                        style={{ color: nav.textColor }}
                        onClick={(e) => {
                            if (isEditMode) e.preventDefault();
                        }}
                    >
                        <i className={platformIcons[icon.platform] || 'fas fa-link'}></i>
                    </a>
                ))}
            </div>
        );
    };
    
    // 🎨 Render CTA Button
    const renderCTAButton = () => {
        if (!nav.ctaButton?.enabled) return null;
        
        // ✅ Mobile visibility check
        if (isMobileView && nav.showCTAButtonMobile === false) {
            return null;
        }
        
        // 🎨 Build dynamic styles
        const ctaStyles = {
            backgroundColor: nav.ctaButton.backgroundColor,
            color: nav.ctaButton.textColor,
            fontFamily: nav.ctaButton.fontFamily || 'inherit',
            fontSize: isMobileView 
                ? `${nav.ctaButton.textSizeMobile || 14}px` 
                : `${nav.ctaButton.textSize || 15}px`,
            
            // 📏 Dimensions
            height: isMobileView 
                ? `${nav.ctaButton.heightMobile ?? nav.ctaButton.height ?? 44}px`
                : `${nav.ctaButton.height ?? 44}px`,
            borderRadius: isMobileView
                ? `${nav.ctaButton.borderRadiusMobile ?? nav.ctaButton.borderRadius ?? 8}px`
                : `${nav.ctaButton.borderRadius ?? 8}px`,
            padding: `0 ${nav.ctaButton.paddingHorizontal ?? 24}px`,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px', // ✅ Gap between text and arrow
            
            // 🎨 Border
            ...(nav.ctaButton.borderEnabled && {
                border: `${nav.ctaButton.borderWidth ?? 2}px solid ${nav.ctaButton.borderColor ?? nav.ctaButton.backgroundColor}`,
            }),
            
            // ✨ Shadow
            ...(nav.ctaButton.shadowEnabled && {
                boxShadow: `0 ${nav.ctaButton.shadowDistance ?? 4}px ${nav.ctaButton.shadowIntensity ?? 10}px rgba(${hexToRgb(nav.ctaButton.shadowColor || '#000000')}, ${(nav.ctaButton.shadowOpacity ?? 20) / 100})`
            })
        };
        
        return (
            <a 
                href={nav.ctaButton.url}
                className="nav-creator-cta"
                style={ctaStyles}
                onClick={(e) => {
                    if (isEditMode) e.preventDefault();
                }}
            >
                {nav.ctaButton.text}
                {renderCTAArrow()}
            </a>
        );
    };
    
    // 🎯 Render CTA Arrow Icon
    const renderCTAArrow = () => {
        if (!nav.ctaButton?.arrowEnabled) return null;
        
        const arrowIcon = nav.ctaButton.arrowIcon || 'arrow_1';
        const arrowSize = nav.ctaButton.arrowSize || 24;
        const arrowInvert = nav.ctaButton.arrowInvert || false;
        const arrowDirection = nav.ctaButton.arrowDirection || 'right';
        
        // ✅ Rotation based on direction
        const rotations = {
            left: 'rotate(180deg)',
            right: 'rotate(0deg)',
            up: 'rotate(-90deg)',
            down: 'rotate(90deg)'
        };
        
        return (
            <img 
                src={`/img/${arrowIcon}.svg`}
                alt="Arrow"
                style={{
                    width: `${arrowSize}px`,
                    height: `${arrowSize}px`,
                    transform: rotations[arrowDirection],
                    filter: arrowInvert ? 'invert(1)' : 'none',
                    transition: 'transform 0.2s ease'
                }}
            />
        );
    };
    
    // 🎨 Helper: Convert Hex to RGB (for shadow)
    const hexToRgb = (hex) => {
        if (!hex) return '0, 0, 0';
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
            ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
            : '0, 0, 0';
    };
    
    // 🎯 Handle Edit Click
    const handleEditClick = (e) => {
        if (!isEditMode) return;
        e.stopPropagation();
        setIsToolbarVisible(true);
    };
    
    // 🎯 Layout Logic - Responsive & Smart
    const getLayoutClass = () => {
        if (isMobileView) {
            return 'nav-creator--mobile';
        }
        return 'nav-creator--desktop';
    };
    
    // 🎨 Flexible Layout System - Ordnet Elemente nach Position an (Responsive!)
    const getLayoutSections = () => {
        const sections = {
            left: [],
            center: [],
            right: []
        };
        
        // ✅ Logo - Separate Mobile Position
        if (nav.logo) {
            const logoPos = isMobileView 
                ? (nav.logoPositionMobile || nav.logoPosition || 'left')
                : (nav.logoPosition || 'left');
            sections[logoPos].push('logo');
        }
        
        // ✅ Navigation Links - Separate Mobile Position
        if (nav.navLinksVisible) {
            const linksPos = isMobileView 
                ? (nav.navLinksPositionMobile || nav.navLinksPosition || 'center')
                : (nav.navLinksPosition || 'center');
            sections[linksPos].push('links');
        }
        
        // ✅ Social Icons - Separate Mobile Position
        if (nav.socialIconsVisible) {
            const socialPos = isMobileView 
                ? (nav.socialIconsPositionMobile || nav.socialIconsPosition || 'right')
                : (nav.socialIconsPosition || 'right');
            sections[socialPos].push('social');
        }
        
        // ✅ CTA Button - Separate Mobile Position
        if (nav.ctaButton?.enabled) {
            const ctaPos = isMobileView 
                ? (nav.ctaButtonPositionMobile || nav.ctaButtonPosition || 'right')
                : (nav.ctaButtonPosition || 'right');
            sections[ctaPos].push('cta');
        }
        
        return sections;
    };
    
    const layoutSections = getLayoutSections();
    
    // 🎯 Helper: Check if section has visible items (berücksichtigt Mobile Visibility)
    const hasVisibleItems = (sectionItems) => {
        return sectionItems.some(item => {
            if (item === 'logo') return nav.logo && (!isMobileView || nav.showLogoMobile !== false);
            if (item === 'links') return nav.navLinksVisible && !isMobileView; // Links nur Desktop wenn kein Hamburger
            if (item === 'social') return nav.socialIconsVisible && (!isMobileView || nav.showSocialIconsMobile !== false);
            if (item === 'cta') return nav.ctaButton?.enabled && (!isMobileView || nav.showCTAButtonMobile !== false);
            return false;
        });
    };
    
    return (
        <>
            {/* ✅ Sticky Placeholder - Verhindert Layout-Jump wenn Navigation sticky wird */}
            {isSticky && (
                <div 
                    className="nav-creator-placeholder"
                    style={{ height: `${activeHeight}px` }}
                />
            )}
            
            <div 
                ref={navRef}
                className={`nav-creator ${getLayoutClass()} ${isSticky ? 'nav-creator--sticky' : ''} ${isEditMode ? 'nav-creator--edit-mode' : ''} ${isHidden ? 'nav-creator--hidden' : ''}`}
                style={{
                    backgroundColor: getBackgroundColor(),
                    height: `${activeHeight}px`,
                    paddingLeft: `${activePaddingLeft}px`,
                    paddingRight: `${activePaddingRight}px`,
                    boxShadow: nav.shadow ? '0 2px 10px rgba(0,0,0,0.1)' : 'none',
                    borderBottom: nav.borderBottomEnabled ? `${nav.borderBottomWidth}px solid ${nav.borderBottomColor}` : 'none',
                    backdropFilter: nav.backdropFilterEnabled ? `blur(${nav.backdropFilterBlur}px)` : 'none',
                    WebkitBackdropFilter: nav.backdropFilterEnabled ? `blur(${nav.backdropFilterBlur}px)` : 'none',
                    ...(isSticky && { 
                        width: stickyWidth,
                        // ✅ CRITICAL FIX: Berechne left-Position basierend auf BEIDEN Sidebars
                        // Rechte Sidebar (Widget Edit): Geschlossen = +110px
                        // Linke Sidebar (Add Widgets): Geschlossen = -110px
                        left: isEditMode ? `calc(50% + ${
                            (isWidgetEditCollapsed ? 110 : 0) + (isAddWidgetsCollapsed ? -110 : 0)
                        }px)` : '50%'
                    })
                }}
                onClick={handleEditClick}
            >
                {/* Edit Overlay */}
                {isEditMode && (
                    <div className="nav-creator-edit-overlay">
                        <button 
                            className="nav-creator-edit-btn"
                            onClick={handleEditClick}
                        >
                            <i className="fas fa-cog"></i>
                            <span>Edit Navigation</span>
                        </button>
                    </div>
                )}
                
                {/* 🎨 Flexible Navigation Content - Basierend auf Position-Settings */}
                {/* ✅ Grid mit 1fr auto 1fr für perfekte Zentrierung der mittleren Sektion */}
                <div 
                    className="nav-creator-content"
                    data-symmetric={nav.symmetricLayout ? 'true' : 'false'}
                >
                    {/* Left Section - IMMER rendern (auch wenn leer) für Grid-Layout */}
                    <div className="nav-creator-section nav-creator-section--left">
                        {hasVisibleItems(layoutSections.left) && layoutSections.left.map((item) => {
                            if (item === 'logo') return <div key="logo" className="nav-creator-item">{renderLogo()}</div>;
                            if (item === 'links' && !isMobileView) return <div key="links" className="nav-creator-item">{renderNavLinks()}</div>;
                            if (item === 'social') return <div key="social" className="nav-creator-item">{renderSocialIcons()}</div>;
                            if (item === 'cta') return <div key="cta" className="nav-creator-item">{renderCTAButton()}</div>;
                            return null;
                        })}
                    </div>
                    
                    {/* Center Section - IMMER rendern (auch wenn leer) für Grid-Layout */}
                    <div className="nav-creator-section nav-creator-section--center">
                        {hasVisibleItems(layoutSections.center) && layoutSections.center.map((item) => {
                            if (item === 'logo') return <div key="logo" className="nav-creator-item">{renderLogo()}</div>;
                            if (item === 'links' && !isMobileView) return <div key="links" className="nav-creator-item">{renderNavLinks()}</div>;
                            if (item === 'social') return <div key="social" className="nav-creator-item">{renderSocialIcons()}</div>;
                            if (item === 'cta') return <div key="cta" className="nav-creator-item">{renderCTAButton()}</div>;
                            return null;
                        })}
                    </div>
                    
                    {/* Right Section - IMMER rendern (auch wenn leer) für Grid-Layout */}
                    <div className="nav-creator-section nav-creator-section--right">
                        {hasVisibleItems(layoutSections.right) && layoutSections.right.map((item) => {
                            if (item === 'logo') return <div key="logo" className="nav-creator-item">{renderLogo()}</div>;
                            if (item === 'links' && !isMobileView) return <div key="links" className="nav-creator-item">{renderNavLinks()}</div>;
                            if (item === 'social') return <div key="social" className="nav-creator-item">{renderSocialIcons()}</div>;
                            if (item === 'cta') return <div key="cta" className="nav-creator-item">{renderCTAButton()}</div>;
                            return null;
                        })}
                    </div>
                </div>
                
                {/* Mobile Menu Button */}
                {isMobileView && nav.navLinksVisible && nav.useMobileMenu && (
                    <button 
                        className="nav-creator-mobile-menu-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsMobileMenuOpen(true);
                        }}
                        style={{
                            width: `${nav.mobileMenuButtonSize || 44}px`,
                            height: `${nav.mobileMenuButtonSize || 44}px`,
                            fontSize: `${(nav.mobileMenuButtonSize || 44) * 0.45}px` // Icon size = 45% of button size
                        }}
                    >
                        <i className="fas fa-bars"></i>
                    </button>
                )}
            </div>
            
            {/* Floating Toolbar */}
            {isEditMode && (
                <NavigationFloatingToolbar
                    isVisible={isToolbarVisible}
                    navigationData={nav}
                    onUpdate={onUpdate}
                    onClose={() => setIsToolbarVisible(false)}
                    colors={colors}
                    spaceFonts={spaceFonts}
                    isMobileView={isMobileView}
                    navRef={navRef}
                    onLogoUpload={handleLogoUpload}
                    isWidgetEditCollapsed={isWidgetEditCollapsed}
                    isAddWidgetsCollapsed={isAddWidgetsCollapsed}
                />
            )}
            
            {/* 📱 Mobile Menu Full Screen Overlay - Portal zu page-preview */}
            {isMobileView && nav.useMobileMenu && isMobileMenuOpen && portalContainer && ReactDOM.createPortal(
                    <>
                        {/* Backdrop */}
                        <div 
                            className="nav-creator-mobile-menu-backdrop"
                            onClick={() => setIsMobileMenuOpen(false)}
                            style={{
                                // ✅ Backdrop Color & Opacity
                                backgroundColor: (() => {
                                    const backdropColor = nav.mobileMenuBackdropColor || '#000000';
                                    const backdropOpacity = (nav.mobileMenuBackdropOpacity || 50) / 100;
                                    
                                    // Convert Hex zu RGBA
                                    if (backdropColor.startsWith('#')) {
                                        const r = parseInt(backdropColor.slice(1, 3), 16);
                                        const g = parseInt(backdropColor.slice(3, 5), 16);
                                        const b = parseInt(backdropColor.slice(5, 7), 16);
                                        return `rgba(${r}, ${g}, ${b}, ${backdropOpacity})`;
                                    }
                                    return `rgba(0, 0, 0, ${backdropOpacity})`;
                                })(),
                                // ✅ Animation Speed
                                animation: `navBackdropFadeIn ${nav.mobileMenuAnimationSpeed || 300}ms ease`
                            }}
                        />
                        
                        {/* Menu Panel */}
                        <div 
                            className="nav-creator-mobile-menu"
                            data-dark={nav.mobileMenuTextColor && nav.mobileMenuTextColor.includes('fff') ? 'true' : 'false'}
                            style={{
                                backgroundColor: nav.mobileMenuBackgroundColor || nav.backgroundColor,
                                borderLeft: nav.mobileMenuBorderEnabled ? `${nav.mobileMenuBorderWidth}px solid ${nav.mobileMenuBorderColor}` : 'none',
                                // ✅ Custom Shadow mit einstellbaren Werten
                                boxShadow: nav.mobileMenuShadowEnabled ? 
                                    `-4px 0 ${nav.mobileMenuShadowBlur || 12}px rgba(0, 0, 0, ${(nav.mobileMenuShadowOpacity || 15) / 100})` : 
                                    'none',
                                width: `${nav.mobileMenuWidth || 80}%`, // ✅ Einstellbare Breite (60-100%)
                                borderRadius: nav.mobileMenuBorderRadius ? `${nav.mobileMenuBorderRadius}px 0 0 ${nav.mobileMenuBorderRadius}px` : '0', // ✅ Nur links Radius
                                gap: `${nav.mobileMenuLinkSpacing || 4}px`, // ✅ Spacing zwischen Links
                                // ✅ Animation Speed
                                animation: `navMobileMenuSlideIn ${nav.mobileMenuAnimationSpeed || 300}ms cubic-bezier(0.4, 0, 0.2, 1)`
                            }}
                        >
                        {/* Close Button */}
                        <button
                            className="nav-creator-mobile-menu-close"
                            onClick={() => setIsMobileMenuOpen(false)}
                            style={{
                                color: nav.mobileMenuTextColor || nav.textColor
                            }}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                        
                        {/* Logo im Mobile Menu (optional) */}
                        {nav.mobileMenuShowLogo && nav.logo && (
                            <div 
                                className="nav-creator-mobile-menu-logo-wrapper"
                                style={{
                                    justifyContent: nav.mobileMenuLogoAlignment === 'left' ? 'flex-start' : 
                                                   nav.mobileMenuLogoAlignment === 'right' ? 'flex-end' : 'center',
                                    paddingLeft: nav.mobileMenuLogoAlignment === 'left' ? `${nav.mobileMenuPadding || 20}px` : 
                                                nav.mobileMenuLogoAlignment === 'center' ? '0' : '0',
                                    paddingRight: nav.mobileMenuLogoAlignment === 'right' ? `${nav.mobileMenuPadding || 20}px` : 
                                                 nav.mobileMenuLogoAlignment === 'center' ? '0' : '0',
                                    marginTop: `${nav.mobileMenuLogoMarginTop || 20}px`,
                                    marginBottom: `${nav.mobileMenuLogoMarginBottom || 20}px`
                                }}
                            >
                                <img 
                                    src={nav.logo} 
                                    alt="Logo" 
                                    className="nav-creator-mobile-menu-logo"
                                    style={{
                                        width: `${nav.mobileMenuLogoWidth || 100}px`,
                                        height: `${nav.mobileMenuLogoHeight || 40}px`,
                                        objectFit: 'contain'
                                    }}
                                />
                            </div>
                        )}
                        
                        {/* Navigation Links */}
                        {nav.mobileMenuShowLinks && nav.navLinks.filter(link => link.visible).map((link) => {
                            // ✅ Berechne Hover Background mit Opacity
                            const getHoverBackground = () => {
                                if (!nav.mobileMenuLinkHoverBg || nav.mobileMenuLinkHoverBg === 'transparent') {
                                    return 'rgba(0, 0, 0, 0.05)'; // Default
                                }
                                
                                const hoverBg = nav.mobileMenuLinkHoverBg;
                                const hoverOpacity = (nav.mobileMenuLinkHoverOpacity || 5) / 100;
                                
                                // Convert Hex zu RGBA
                                if (hoverBg.startsWith('#')) {
                                    const r = parseInt(hoverBg.slice(1, 3), 16);
                                    const g = parseInt(hoverBg.slice(3, 5), 16);
                                    const b = parseInt(hoverBg.slice(5, 7), 16);
                                    return `rgba(${r}, ${g}, ${b}, ${hoverOpacity})`;
                                }
                                return hoverBg;
                            };
                            
                            return (
                            <a
                                key={link.id}
                                href={link.url}
                                className="nav-creator-mobile-menu-link"
                                style={{
                                    color: nav.mobileMenuTextColor || nav.textColor,
                                    fontSize: `${nav.mobileMenuTextSize || 16}px`,
                                    fontFamily: nav.textFontFamily || 'inherit',
                                    paddingLeft: `${nav.mobileMenuPadding || 20}px`,
                                    paddingRight: `${nav.mobileMenuPadding || 20}px`,
                                    // ✅ CSS Variable für Hover Background
                                    '--hover-bg': getHoverBackground()
                                }}
                                onClick={(e) => {
                                    if (isEditMode) {
                                        e.preventDefault();
                                    } else {
                                        setIsMobileMenuOpen(false);
                                    }
                                }}
                            >
                                {link.text}
                            </a>
                            );
                        })}
                        
                        {/* Social Icons in Mobile Menu (if visible and enabled) */}
                        {nav.mobileMenuShowSocial && nav.socialIconsVisible && nav.showSocialIconsMobile && (
                            <div className="nav-creator-mobile-menu-social">
                                {nav.socialIcons.filter(icon => icon.visible && icon.url).map((icon) => {
                                    const platformIcons = {
                                        facebook: 'fab fa-facebook',
                                        instagram: 'fab fa-instagram',
                                        linkedin: 'fab fa-linkedin',
                                        youtube: 'fab fa-youtube',
                                        twitter: 'fab fa-twitter',
                                        tiktok: 'fab fa-tiktok'
                                    };
                                    
                                    return (
                                        <a
                                            key={icon.id}
                                            href={icon.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="nav-creator-mobile-menu-social-icon"
                                            style={{ color: nav.mobileMenuTextColor || nav.textColor }}
                                            onClick={(e) => {
                                                if (isEditMode) e.preventDefault();
                                            }}
                                        >
                                            <i className={platformIcons[icon.platform] || 'fas fa-link'}></i>
                                        </a>
                                    );
                                })}
                            </div>
                        )}
                        
                        {/* CTA Button in Mobile Menu - Getrennt von showCTAButtonMobile! */}
                        {nav.mobileMenuShowCTA && nav.ctaButton?.enabled && (
                            <a
                                href={nav.ctaButton.url}
                                className="nav-creator-mobile-menu-cta"
                                style={{
                                    backgroundColor: nav.ctaButton.backgroundColor,
                                    color: nav.ctaButton.textColor,
                                    fontFamily: nav.ctaButton.fontFamily || 'inherit',
                                    fontSize: `${nav.ctaButton.textSizeMobile || 14}px`,
                                    marginLeft: `${nav.mobileMenuPadding || 20}px`,
                                    marginRight: `${nav.mobileMenuPadding || 20}px`,
                                    
                                    // 📏 Dimensions (Mobile)
                                    height: `${nav.ctaButton.heightMobile ?? nav.ctaButton.height ?? 44}px`,
                                    borderRadius: `${nav.ctaButton.borderRadiusMobile ?? nav.ctaButton.borderRadius ?? 8}px`,
                                    padding: `0 ${nav.ctaButton.paddingHorizontal ?? 24}px`,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px', // ✅ Gap between text and arrow
                                    
                                    // 🎨 Border
                                    ...(nav.ctaButton.borderEnabled && {
                                        border: `${nav.ctaButton.borderWidth ?? 2}px solid ${nav.ctaButton.borderColor ?? nav.ctaButton.backgroundColor}`,
                                    }),
                                    
                                    // ✨ Shadow
                                    ...(nav.ctaButton.shadowEnabled && {
                                        boxShadow: `0 ${nav.ctaButton.shadowDistance ?? 4}px ${nav.ctaButton.shadowIntensity ?? 10}px rgba(${hexToRgb(nav.ctaButton.shadowColor || '#000000')}, ${(nav.ctaButton.shadowOpacity ?? 20) / 100})`
                                    })
                                }}
                                onClick={(e) => {
                                    if (isEditMode) {
                                        e.preventDefault();
                                    } else {
                                        setIsMobileMenuOpen(false);
                                    }
                                }}
                            >
                                {nav.ctaButton.text}
                                {renderCTAArrow()}
                            </a>
                        )}
                        </div>
                    </>,
                    portalContainer
                )}
        </>
    );
}

export default NewNavCreator;

